/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
import * as React from 'react';
import {styled} from '@mui/material/styles';
import MuiAppBar, {AppBarProps as MuiAppBarProps} from '@mui/material/AppBar';
import {
  Grid,
  IconButton,
  Box,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Link
} from '@mui/material';
import {deepPurple} from '@mui/material/colors';
import {useAuthContext} from '@context/AuthContext';
import {useSelectedCheckBoxContext} from '@context/SelectedCheckBoxContext';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {HelpOutline, Edit, Add, CardGiftcard} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import {jwtDecode} from 'jwt-decode';

import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';

import {
  ToolBarStyle,
  InfoGrid,
  HeadingTypography,
  ContentTypography,
  EncounterContainerDiv,
  FilterDataDiv,
  EncounterDiv,
  HeaderImageGrid,
  PersonalInfoGrid,
  PadButton
} from './TopAppBarStyles';

import {
  getPageData,
  getReferenceData
} from '@shared/services/service-handlers/PrivateService';
import HelperUtils from '@shared/utils/HelperUtils';
import EncryptUtils from 'src/utils/EncryptUtils';
import AuthUtils from 'src/utils/AuthUtils';
import headerImage from 'src/assets/images/header_lines.svg';
import {APP} from 'src/styles/variables';
import {useMsal} from '@azure/msal-react';
import {useIsAuthenticated} from '@azure/msal-react';
import {
  BASE_APPLICATION,
  BASE_ROUTE,
  IS_FROM_EPIC,
  IS_FROM_ATHENA,
  MODE,
  BASE_ROUTE_PAD,
  BASE_ROUTE_WIDGETS,
  BASE_ROUTE_MODULE
} from 'src/routes/BaseRoute';
import FormConstants from '@shared/constants/FormConstants';
import {
  signOutUser,
  adfsLogOutService
} from '@shared/services/service-handlers/PrivateService';
import {useAppContext} from '@context/AppContext';
import {environment} from '@shared/config/environment';
import {PUBLIC_ROUTES} from 'src/routes/RouteConstants';

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open'
})<AppBarProps>(({theme, open}) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: '58px',
  boxShadow: 'none',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }),
  ...(!open && {
    width: `calc(100% - 65px)`
  })
}));

export default function TopAppBar(props: any): React.ReactElement {
  const {
    appState: {applicationName}
  } = useAppContext();
  const [open, setOpen] = React.useState(true);
  const {logout} = useAuthContext();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const [bannerDetailsData, setBannerDetailsData]: any = React.useState({});
  const [defaultData, setDefaultData]: any = React.useState({});
  const [bannerDetailsUIData, setBannerDetailsUIData]: any = React.useState([]);
  const getLocaleStorageData = AuthUtils.getSelectedCheckBoxData();
  const {
    handleMenuAction,
    subModule,
    record_id,
    banner_details,
    handleAddPadAction,
    applicationId,
    padId
  } = props;
  const [filtersData, setFiltersData] = React.useState<any>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const {SelectedCheckBoxState} = useSelectedCheckBoxContext();
  const isAzureAuthenticated = useIsAuthenticated();
  const {instance} = useMsal();

  const isFromEpic = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_EPIC
  );
  const isFromAthena = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_ATHENA
  );
  const mode = EncryptUtils.getDecryptedData(location?.search, MODE);
  let userData: any;

  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = async () => {
    try {
      const data: any = {
        sql_data: {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.LOGOUT_SUCCESS
        }
      };

      if (userData?.user?.type === FormConstants.LOGIN_TYPE.ADFS) {
        await adfsLogOutService({origin: window.location.origin});
        AuthUtils.clearStorage();
        handleClose();
        navigate(`/${BASE_ROUTE}/` + PUBLIC_ROUTES.LOGIN);
        return;
      }

      const encryptRequestData = EncryptUtils.encryptedData(data);
      await signOutUser({
        tk: encryptRequestData
      });

      if (isAzureAuthenticated || AuthUtils.getIsAzureAuthenticated()) {
        AuthUtils.clearStorage();
        navigate(`/${BASE_ROUTE}/` + PUBLIC_ROUTES.LOGIN);
        instance.logoutRedirect({
          postLogoutRedirectUri: '/'
        });
        return;
      } else {
        logout();
      }

      handleClose();
      AuthUtils.clearStorage();
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const handleDrawerOpen = () => {
    setOpen(!open);

    if (typeof handleMenuAction === 'function') {
      handleMenuAction();
    }
  };

  const init = async () => {
    try {
      if (record_id && banner_details?.banner?.sql_data.sql) {
        const data = {
          sql_data: banner_details?.banner?.sql_data,
          record_id: record_id
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);

        const result = await getPageData(
          applicationName,
          props.module,
          props.sub_module,
          {
            tk: encryptRequestData
          }
        );
        if (banner_details?.banner?.fields) {
          banner_details.banner.fields.forEach((field: any) => {
            if (field?.type && field?.data_column) {
              if (result?.data) {
                result.data.forEach((item: any) => {
                  item[field.data_column] = HelperUtils.formatData(
                    item[field.data_column],
                    field.type,
                    field.formatter
                  );
                });
              }
            }
          });
        }
        setBannerDetailsUIData(banner_details?.banner?.fields);
        setBannerDetailsData(result?.data?.[0]);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner_details]);

  const initFilterData = () => {
    if (getLocaleStorageData) {
      let filtersDataCount = 0;
      const filterData = JSON.parse(getLocaleStorageData);
      if (filterData) {
        const checkedRowIds = filterData?.map((row: any) => {
          return row?.ID;
        });
        if (checkedRowIds && checkedRowIds.length)
          filtersDataCount = checkedRowIds.length;
        if (checkedRowIds.length === 0) {
          filtersDataCount = 0;
        }
      }
      setFiltersData(filtersDataCount);
    }
  };

  const navigateHelpDeskUrl = () => {
    window.open(defaultData?.help_desk_url, '_blank');
  };

  const navigateMarketPlace = () => {
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${FormConstants.MODULE.MARKETPLACE}`
    );
  };

  const getDefaultData = async () => {
    try {
      const res = await getReferenceData();
      setDefaultData(res?.data);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  React.useEffect(() => {
    initFilterData();
  }, [SelectedCheckBoxState]);

  React.useEffect(() => {
    getDefaultData();
  }, []);

  const addPad = (): void => {
    if (typeof handleAddPadAction === 'function') {
      handleAddPadAction();
    }
  };

  const editPad = (): void => {
    navigate(
      `/${BASE_ROUTE}/${BASE_APPLICATION}/${applicationId}/${BASE_ROUTE_PAD}/${padId}/${BASE_ROUTE_WIDGETS}`
    );
  };

  const savePad = (): void => {
    navigate(
      `/${BASE_ROUTE}/${BASE_APPLICATION}/${applicationId}/${BASE_ROUTE_PAD}/${padId}`
    );
  };

  const getFirstCharacters = (data: any) => {
    const name =
      data?.name ||
      (userData?.user?.name ??
        userData?.user?.first_name + ' ' + userData?.user?.last_name);

    return name.match(/\b\w/g).join('');
  };

  const renderMenu = (): React.ReactElement => {
    return (
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            }
          }
        }}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}>
        <MenuItem>
          <Grid container>
            <Grid
              item
              xs={4}
              style={{
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <Avatar
                sx={{bgcolor: deepPurple[500]}}
                style={{width: 60, height: 60}}>
                {getFirstCharacters(userData)}
              </Avatar>
            </Grid>
            <Grid item xs={8}>
              <Typography style={{color: 'black', fontWeight: 'bold'}}>
                {userData?.name ||
                  (userData?.user?.name ??
                    userData?.user?.first_name +
                      ' ' +
                      userData?.user?.last_name)}
              </Typography>

              <Typography
                variant="body2"
                style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {userData?.user?.email || userData?.preferred_username}
              </Typography>

              <Link underline="always" onClick={handleLogOut}>
                Sign Out
              </Link>
            </Grid>
          </Grid>
        </MenuItem>
      </Menu>
    );
  };

  const renderMenuItemList = (): React.ReactElement => {
    return (
      <Grid
        item
        sx={{
          width: '100%',
          marginRight: '1em',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'nowrap',
          padding: 0
        }}
        container
        spacing={2}>
        {subModule &&
          bannerDetailsUIData.length > 0 &&
          getLocaleStorageData &&
          JSON.parse(getLocaleStorageData)?.length > 0 && (
            <InfoGrid item>
              <EncounterContainerDiv>
                <EncounterDiv>
                  <span style={{fontSize: '1.1em'}}>
                    {' '}
                    {props.module === FormConstants.MODULE.CLINICAL
                      ? t('PRIVATE.DASHBOARD.FILTERS.ENCOUNTERS', {
                          ns: 'private'
                        })
                      : props.module === FormConstants.MODULE.PATIENT_ACCOUNTING
                        ? t('PRIVATE.DASHBOARD.FILTERS.ACCOUNTS', {
                            ns: 'private'
                          })
                        : ''}
                  </span>

                  <FilterDataDiv>{filtersData}</FilterDataDiv>
                </EncounterDiv>
              </EncounterContainerDiv>
            </InfoGrid>
          )}

        {subModule &&
          bannerDetailsUIData?.map((item: any, index: number) => {
            return (
              <PersonalInfoGrid key={index} item>
                <HeadingTypography>{item.label}</HeadingTypography>
                <ContentTypography>
                  {bannerDetailsData?.[item?.data_column]}
                </ContentTypography>
              </PersonalInfoGrid>
            );
          })}

        {(isFromEpic || isFromAthena) &&
        mode === FormConstants.STATIC_VALUES.VIEW ? (
          <div />
        ) : (
          <>
            {props?.showEditPadButton && (
              <Grid item sx={{display: 'flex', justifyContent: 'center'}}>
                <PadButton
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => editPad()}>
                  Edit
                </PadButton>
              </Grid>
            )}
            {props?.showAddPadButton && (
              <Grid item sx={{display: 'flex', justifyContent: 'center'}}>
                <PadButton
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => addPad()}>
                  Pad
                </PadButton>
              </Grid>
            )}
            {props?.showWidgetsSaveButton && (
              <Grid item sx={{display: 'flex', justifyContent: 'center'}}>
                <PadButton variant="outlined" onClick={() => savePad()}>
                  Save
                </PadButton>
              </Grid>
            )}
            {!environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED &&
            environment?.hide_modules?.marketplace ? null : (
              <Grid item sx={{display: 'flex', justifyContent: 'center'}}>
                <Tooltip
                  title={'Marketplace'}
                  placement="bottom"
                  enterDelay={100}
                  leaveDelay={50}>
                  <IconButton
                    color="inherit"
                    aria-label="marketplace"
                    onClick={navigateMarketPlace}
                    size="large">
                    <CardGiftcard />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {environment?.hide_modules?.help_desk ? null : (
              <Grid item sx={{display: 'flex', justifyContent: 'center'}}>
                <Tooltip
                  title={'Help'}
                  placement="bottom"
                  enterDelay={100}
                  leaveDelay={50}>
                  <IconButton
                    color="inherit"
                    aria-label="help"
                    size="large"
                    onClick={navigateHelpDeskUrl}>
                    <HelpOutline />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            <Grid item sx={{display: 'flex', justifyContent: 'center'}}>
              <Tooltip title="Settings">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ml: 2}}
                  aria-controls={anchorEl ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={anchorEl ? 'true' : undefined}>
                  <Avatar sx={{bgcolor: deepPurple[500]}}>
                    {getFirstCharacters(userData)}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const renderTopAppBar = (): React.ReactElement => {
    return (
      <AppBar
        open={open}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          background: `${APP.PALETTE.APP_BAR_COLOR.BACKGROUND}`
        }}>
        <ToolBarStyle>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5
            }}>
            <MenuIcon />
          </IconButton>

          <HeaderImageGrid
            sx={{
              position: 'absolute',
              backgroundImage: `url(${headerImage})`
            }}
          />
          {renderMenuItemList()}
        </ToolBarStyle>
      </AppBar>
    );
  };

  return (
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
      {renderTopAppBar()}
      {Boolean(anchorEl) && renderMenu()}
    </Box>
  );
}

